import React, { useEffect, useMemo, useState } from "react";
import TabItems from "../../../../core/common/TabItems";
import { Link } from "react-router-dom";
import {
  DownloadOutlined,
  CloseOutlined,
  UserOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  ReadOutlined,
  SolutionOutlined,
  BankOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileUnknownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import useApi from "../../../../core/customhook/useApi";
import "./CandidateApplicationForm.css";
import { apibaseurl } from "../../../../core/webservice/Webservice_url";
import { exportAntdTableToExcel } from "../../../../core/utils/exportExcel";
import dayjs from "dayjs";
import Loading from "../../../../core/common/custom-loading/Loading";

const modalTabs = [
  { id: "application", label: "Application" },
  { id: "address", label: "Address" },
  { id: "consent", label: "Consent" },
  { id: "candidateDocuments", label: "Candidate Documents" },
];

interface CandidateDocumentsModalProps {
  isOpen: boolean;
  handleClose: () => void;
  assignmentId: string;
}

const getSectionIcon = (name?: string) => {
  const key = (name || "").toLowerCase();
  if (key.includes("aadhaar") || key.includes("kyc"))
    return <SafetyCertificateOutlined />;
  if (key.includes("pan")) return <IdcardOutlined />;
  if (
    key.includes("education") ||
    key.includes("degree") ||
    key.includes("academic") ||
    key.includes("pg") ||
    key.includes("marksheet")
  )
    return <ReadOutlined />;
  if (
    key.includes("employ") ||
    key.includes("experience") ||
    key.includes("work") ||
    key.includes("pay")
  )
    return <SolutionOutlined />;
  if (key.includes("address") || key.includes("bank")) return <BankOutlined />;
  if (
    key.includes("criminal") ||
    key.includes("court") ||
    key.includes("police")
  )
    return <FileProtectOutlined />;
  return <FileSearchOutlined />;
};

// Presentational-only helper: picks an icon for a file based on its mime type / name.
const getFileIcon = (mimeType?: string, name?: string) => {
  const type = (mimeType || "").toLowerCase();
  const fileName = (name || "").toLowerCase();
  if (type.includes("pdf") || fileName.endsWith(".pdf"))
    return <FilePdfOutlined />;
  if (type.includes("image") || /\.(jpg|jpeg|png|gif|webp)$/.test(fileName))
    return <FileImageOutlined />;
  if (
    type.includes("word") ||
    fileName.endsWith(".doc") ||
    fileName.endsWith(".docx")
  )
    return <FileWordOutlined />;
  return <FileUnknownOutlined />;
};

// Presentational-only helper: formats byte size into a readable string.
const formatFileSize = (bytes?: string | number) => {
  const size = Number(bytes);
  if (!size || isNaN(size)) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Presentational-only helper: formats an ISO date string.
const formatFileDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

// Presentational-only helper: scan status badge icon + label.
const getScanStatusMeta = (status?: string) => {
  const key = (status || "").toLowerCase();
  if (key === "completed" || key === "clean" || key === "passed") {
    return {
      label: "Scanned",
      icon: <CheckCircleOutlined />,
      className: "caf-scan-ok",
    };
  }
  if (key === "failed" || key === "infected" || key === "rejected") {
    return {
      label: "Failed",
      icon: <CloseCircleOutlined />,
      className: "caf-scan-failed",
    };
  }
  // return {
  //   label: "Pending Scan",
  //   icon: <ClockCircleOutlined />,
  //   className: "caf-scan-pending",
  // };
};

// --- Document preview/download helpers (new) -------------------------------

type PreviewFileType = "image" | "pdf" | "other";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const getFileExtension = (name?: string) => {
  if (!name) return "";
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

const getPreviewFileType = (
  mimeType?: string,
  name?: string,
): PreviewFileType => {
  const type = (mimeType || "").toLowerCase();
  const ext = getFileExtension(name);

  if (type.includes("pdf") || ext === "pdf") return "pdf";
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext))
    return "image";
  return "other";
};

const getFileUrl = (file: any) => `${apibaseurl}/uploads/${file.s3_key}`;

/**
 * response_data of a check can come in two shapes:
 *  - Object  -> single entry   { company_name: "...", ... }
 *  - Array   -> multiple entry [ { ... }, { ... } ]
 * This normalises both into an array of entry objects, so the UI
 * and the Excel export can handle them the same way.
 */
const getCheckEntries = (responseData: any): Record<string, any>[] => {
  if (!responseData) return [];

  // Case 1: response_data is already an array of objects
  if (Array.isArray(responseData)) {
    return responseData.filter(
      (entry) => entry && typeof entry === "object",
    );
  }

  // Ignore non-object values
  if (typeof responseData !== "object") {
    return [];
  }

  // Case 2: response_data is a single object containing fields
  // Example: { company_name: "Digi India", pincode: "110085" }
  const values = Object.values(responseData);

  const hasIndexedObjectEntries =
    values.length > 0 &&
    values.every(
      (value) => value && typeof value === "object" && !Array.isArray(value),
    ) &&
    Object.keys(responseData).every((key) => /^\d+$/.test(key));

  // Case 3: response_data contains indexed objects
  // Example: { "0": {...}, "1": {...} }
  if (hasIndexedObjectEntries) {
    return values as Record<string, any>[];
  }

  // Otherwise, treat the whole object as one entry
  return [responseData];
};

const CandidateApplicationForm = ({
  isOpen,
  handleClose,
  assignmentId,
}: CandidateDocumentsModalProps) => {
  const [activeTab, setActiveTab] = useState(modalTabs[0]?.id);
  const [brokenImageIds, setBrokenImageIds] = useState<Set<string>>(new Set());
  const [address, setAddress] = useState<any[]>([]);
  const markImageBroken = (fileId: string) => {
    setBrokenImageIds((prev) => {
      if (prev.has(fileId)) return prev;
      const next = new Set(prev);
      next.add(fileId);
      return next;
    });
  };

  const getCandidateResponse = useApi<any>(
    `/api/v1/candidate/forms/candidate-response/${assignmentId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const getAddress = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getDocumentAPI = useApi<any>(
    `/api/v1/candidate/forms/uploaded-files/${assignmentId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const getConcentAPI = useApi<any>(
    `/api/v1/candidate/forms/concent/${assignmentId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
      responseType: "blob",
    },
  );
   
  const [consentPdf, setConsentPdf] = useState("");
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [consentReload, setConsentReload] = useState(0);

  useEffect(() => {
    if (!isOpen || !assignmentId) return;

    let cancelled = false;
    let objectUrl = "";

    const loadConsent = async () => {
      setConsentLoading(true);
      setConsentError("");
      setConsentPdf("");

      try {
        const res: any = await getConcentAPI.callApi();
        if (cancelled) return;

        const raw = res instanceof Blob ? res : res?.data;
        if (!(raw instanceof Blob) || raw.size === 0) {
          throw new Error("Consent document is not available yet.");
        }

        // backend ne JSON error bheja ho to uska message dikhao
        if (raw.type.includes("json") || raw.type.includes("text")) {
          let message = "Unable to load consent document.";
          try {
            const body = JSON.parse(await raw.text());
            message = body?.error || body?.message || message;
          } catch {
            /* ignore */
          }
          throw new Error(message);
        }

        // type force karo, taaki viewer PDF hi samjhe
        const pdfBlob = new Blob([raw], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setConsentPdf(objectUrl);
      } catch (error: any) {
        if (cancelled) return;
        setConsentError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load consent document.",
        );
      } finally {
        if (!cancelled) setConsentLoading(false);
      }
    };

    loadConsent();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assignmentId, consentReload]);

  useEffect(() => {
    if (isOpen && assignmentId) {
      getConcentAPI.callApi();
    }
  }, [isOpen, assignmentId]);

  const fetchAddresss = async (candidateId: string) => {
    try {
      const res = await getAddress.callApi(
        undefined,
        `/api/v1/candidates/addresses/${candidateId}`,
      );

      setAddress(res?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const candidate = getCandidateResponse?.data?.data?.candidate;
  const candidateId = candidate?.candidate_id;

  useEffect(() => {
    if (isOpen && candidateId) {
      fetchAddresss(candidateId);
    }
  }, [isOpen, candidateId]);

  useEffect(() => {
    if (isOpen && assignmentId) {
      getCandidateResponse.callApi();
    }
  }, [isOpen, assignmentId]);
  // const candidate = getCandidateResponse.data?.data?.candidate;

  useEffect(() => {
    if (isOpen && assignmentId) {
      getDocumentAPI.callApi();
    }
  }, [isOpen, assignmentId]);

  const candidateDocuments = getDocumentAPI.data?.data || [];

  const HIDDEN_CHECKS = ["address verification"];

  const checks = useMemo(
    () =>
      (getCandidateResponse?.data?.data?.checks || []).filter(
        (check: any) =>
          !HIDDEN_CHECKS.includes(
            (check.check_name || "").trim().toLowerCase(),
          ),
      ),
    [getCandidateResponse?.data?.data?.checks],
  );
  // const checks = getCandidateResponse?.data?.data?.checks || [];

  const formatKey = (key: string) =>
    key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

  const formatValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const lowerKey = key.toLowerCase();

    // Only actual date fields
    if (
      lowerKey === "dob" ||
      lowerKey === "date" ||
      lowerKey.endsWith("_date") ||
      lowerKey.endsWith("_at")
    ) {
      const date = new Date(value);

      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-GB");
      }
    }
 
    return String(value);
  };

  const columns = useMemo(
    () => [
      { title: "Section", dataIndex: "section", key: "section" },
      { title: "Field", dataIndex: "field", key: "field" },
      { title: "Value", dataIndex: "value", key: "value" },
    ],
    [],
  );

  const addressExcelColumns = useMemo(
    () => [
      {
        title: "Address Type",
        dataIndex: "address_type",
        key: "address_type",
      },
      {
        title: "Address Line 1",
        dataIndex: "address_line1",
        key: "address_line1",
      },
      {
        title: "Address Line 2",
        dataIndex: "address_line2",
        key: "address_line2",
      },
      {
        title: "Address Line 3",
        dataIndex: "address_line3",
        key: "address_line3",
      },
      {
        title: "City",
        dataIndex: "city",
        key: "city",
      },
      {
        title: "State",
        dataIndex: "state",
        key: "state",
      },
      {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
      },
      {
        title: "Resolved Address",
        dataIndex: "resolved_address",
        key: "resolved_address",
      },
       
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
      },
      {
        title: "Period of Stay",
        dataIndex: "period_of_stay",
        key: "period_of_stay",
      },
      {
        title: "Ownership Status",
        dataIndex: "ownership_status",
        key: "ownership_status",
      },
      {
        title: "Verifier Name",
        dataIndex: "verifier_full_name",
        key: "verifier_full_name",
      },
      {
        title: "Verifier Relationship",
        dataIndex: "verifier_relationship",
        key: "verifier_relationship",
      },
      {
        title: "Verifier Landmark",
        dataIndex: "verifier_addrlandmark",
        key: "verifier_addrlandmark",
      },
      {
        title: "Live Selfie Verified",
        dataIndex: "live_selfie_verified",
        key: "live_selfie_verified",
      },
      {
        title: "Live Selfie URL",
        dataIndex: "live_selfie_url",
        key: "live_selfie_url",
      },
      {
        title: "Digital Signature URL",
        dataIndex: "digital_signature_url",
        key: "digital_signature_url",
      },
      {
        title: "ID Proof",
        dataIndex: "id_proof_photo",
        key: "id_proof_photo",
      },
      {
        title: "Address Proof",
        dataIndex: "address_proof_photo",
        key: "address_proof_photo",
      },
      {
        title: "House Photo",
        dataIndex: "house_photo",
        key: "house_photo",
      },
      {
        title: "Locality Photo",
        dataIndex: "locality_photo",
        key: "locality_photo",
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
      },
    ],
    [],
  );

  const handleDownloadAddressExcel = () => {
    if (!address || address.length === 0) {
      return;
    }

    const addressData = address.map((item: any) => ({
      address_type: item.address_type || "-",
      address_line1: item.address_line1 || "-",
      address_line2: item.address_line2 || "-",
      address_line3: item.address_line3 || "-",
      city: item.city || "-",
      state: item.state || "-",
      pincode: item.pincode || "-",
      country: item.country || "-",
      resolved_address: item.resolved_address || "-",
      address_latitude: item.address_latitude || "-",
      address_longitude: item.address_longitude || "-",
      stage: item.stage ? item.stage.replaceAll("_", " ") : "-",
      status: item.status || "-",
      period_of_stay: item.period_of_stay || "-",
      ownership_status: item.ownership_status || "-",
      verifier_full_name: item.verifier_full_name || "-",
      verifier_relationship: item.verifier_relationship || "-",
      verifier_addrlandmark: item.verifier_addrlandmark || "-",

      live_selfie_verified: item.live_selfie_verified ? "Yes" : "No",

      live_selfie_url: item.live_selfie_url
        ? `${apibaseurl}${item.live_selfie_url}`
        : "-",

      digital_signature_url: item.digital_signature_url
        ? `${apibaseurl}${item.digital_signature_url}`
        : "-",

      id_proof_photo: item.id_proof_photo
        ? `${apibaseurl}${item.id_proof_photo}`
        : "-",

      address_proof_photo: item.address_proof_photo
        ? `${apibaseurl}${item.address_proof_photo}`
        : "-",

      house_photo: item.house_photo ? `${apibaseurl}${item.house_photo}` : "-",

      locality_photo: item.locality_photo
        ? `${apibaseurl}${item.locality_photo}`
        : "-",

      created_at: item.created_at
        ? dayjs(item.created_at).format("DD-MM-YYYY")
        : "-",
    }));

    exportAntdTableToExcel({
      columns: addressExcelColumns,
      dataSource: addressData,
      fileName: "Candidate_Address_Report",
      sheetName: "Address Details",
    });
  };

  const filteredData = useMemo(() => {
    const rows: { section: string; field: string; value: string }[] = [];

    if (candidate) {
      Object.entries(candidate).forEach(([key, value]) => {
        rows.push({
          section: "Personal Details",
          field: formatKey(key),
          value: formatValue(key, value),
        });
      });
    }

    checks.forEach((check: any) => {
      // Works for both: single object and array of multiple entries
      const entries = getCheckEntries(check.response_data);
      const isMultiple = entries.length > 1;

      entries.forEach((entry, index) => {
        const sectionName = isMultiple
          ? `${check.check_name} (${index + 1} of ${entries.length})`
          : check.check_name;

        Object.entries(entry).forEach(([key, value]) => {
          rows.push({
            section: sectionName,
            field: formatKey(key),
            value: formatValue(key, value),
          });
        });
      });
    });

    return rows;
  }, [candidate, checks]);

  const handleDownloadFile = async (file: any) => {
    try {
      const response = await fetch(getFileUrl(file), { method: "GET" });
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file", err);
    }
  };

  if (!isOpen) return <></>;
  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Candidate_Details_Report",
      sheetName: "Candidate Details",
    });
  };
  return (
    <>
      <div className="modal-backdrop fade show" onClick={handleClose} />
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content caf-modal-content">
            <div className="modal-header border-0 d-flex flex-column align-items-stretch pb-0 caf-modal-header">
              <div className="caf-modal-header-top">
                <div className="caf-modal-title-group">
                  <div className="caf-modal-title-icon">
                    <UserOutlined />
                  </div>
                  <div className="caf-modal-title-text">
                    <h5>Candidate Verification</h5>
                    <span>Complete profile &amp; background check summary</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="caf-btn-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <CloseOutlined />
                </button>
              </div>

              {/* Tabs header */}
              <div className="caf-tabs">
                <TabItems
                  tabList={modalTabs}
                  activeTab={activeTab}
                  handleClick={(d: any) => setActiveTab(d.id)}
                />
              </div>
            </div>

            <div className="modal-body caf-modal-body">
              {/* --- application --- */}

              {activeTab === "application" &&
                (getCandidateResponse.loading ? (
                  <Loading />
                ) : (
                  <>
                    {candidate && (
                      <div className="caf-section-card">
                        <div className="caf-section-header">
                          <div className="caf-section-header-left">
                            <div className="caf-section-icon">
                              <UserOutlined />
                            </div>
                            <div className="caf-section-titles">
                              <p className="caf-section-title">
                                Candidate Details
                              </p>
                              <p className="caf-section-subtitle">
                                Basic profile information
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="caf-field-grid">
                          {Object.entries(candidate).map(([key, value]) => (
                            <div className="caf-field-box" key={key}>
                              <p className="caf-field-label">
                                {formatKey(key)}
                              </p>
                              <p className="caf-field-value">
                                {formatValue(key, value)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                {checks?.map((check: any) => {
  const entries = getCheckEntries(check.response_data);
  const isMultiple = entries.length > 1;

  return (
    <div
      key={check.assignment_template_id}
      className="caf-section-card"
    >
      <div className="caf-section-header">
        <div className="caf-section-header-left">
          <div className="caf-section-icon">
            {getSectionIcon(check.check_name)}
          </div>

          <div className="caf-section-titles">
            <p className="caf-section-title">{check.check_name}</p>
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="caf-empty-state">No Data Available</div>
      ) : (
        entries.map((entry, entryIndex) => (
          <div
            key={entryIndex}
            className={isMultiple ? "mb-3" : ""}
          >
            {isMultiple && (
              <div
                className="px-3 py-2"
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#344054",
                  background: "#F9FAFB",
                  borderBottom: "1px solid #EAECF0",
                }}
              >
                {check.check_name} ({entryIndex + 1} of {entries.length})
              </div>
            )}

            <div className="caf-field-grid">
              {Object.entries(entry).map(([key, value]: any) => (
                <div className="caf-field-box" key={key}>
                  <p className="caf-field-label">{formatKey(key)}</p>
                  <p className="caf-field-value">
                    {formatValue(key, value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
})}

                    <div className="caf-footer-actions">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="caf-btn caf-btn-secondary"
                      >
                        <CloseOutlined />
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadExcel}
                        className="caf-btn caf-btn-primary"
                      >
                        <DownloadOutlined />
                        Export to Excel
                      </button>
                    </div>
                  </>
                ))}

              {activeTab === "address" && (
                <div className="px-1 pb-4">
                  {/* Address Header + Download Button */}

                  {/* Loading */}
                  {getAddress.loading ? (
                    // <div className="caf-section-card">
                    //   <div className="caf-empty-state">
                    //     Loading address information...
                    //   </div>
                    // </div>
                    <Loading />
                  ) : address.length === 0 ? (
                    <div className="caf-section-card">
                      <div className="caf-empty-state">
                        No Address Information Available
                      </div>
                    </div>
                  ) : (
                    address.map((addressItem: any, index: number) => (
                      <div
                        className="caf-section-card"
                        key={addressItem.address_id || index}
                      >
                        {/* Address Header */}
                        <div className="caf-section-header">
                          <div className="caf-section-header-left">
                            <div className="caf-section-icon">
                              <BankOutlined />
                            </div>

                            <div className="caf-section-titles">
                              <p className="caf-section-title">
                                Address Information
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Address Fields */}
                        <div className="caf-field-grid">
                          {/* Address Type */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Address Type</p>

                            <p className="caf-field-value">
                              {addressItem.address_type || "-"}
                            </p>
                          </div>

                          {/* Address Line 1 */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Address Line 1</p>

                            <p className="caf-field-value">
                              {addressItem.address_line1 || "-"}
                            </p>
                          </div>

                          {/* Address Line 2 */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Address Line 2</p>

                            <p className="caf-field-value">
                              {addressItem.address_line2 || "-"}
                            </p>
                          </div>

                          {/* Address Line 3 */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Address Line 3</p>

                            <p className="caf-field-value">
                              {addressItem.address_line3 || "-"}
                            </p>
                          </div>

                          {/* City */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">City</p>

                            <p className="caf-field-value">
                              {addressItem.city || "-"}
                            </p>
                          </div>

                          {/* State */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">State</p>

                            <p className="caf-field-value">
                              {addressItem.state || "-"}
                            </p>
                          </div>

                          {/* Pincode */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Pincode</p>

                            <p className="caf-field-value">
                              {addressItem.pincode || "-"}
                            </p>
                          </div>

                          {/* Country */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Country</p>

                            <p className="caf-field-value">
                              {addressItem.country || "-"}
                            </p>
                          </div>

                          {/* Resolved Address */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Resolved Address</p>

                            <p className="caf-field-value">
                              {addressItem.resolved_address || "-"}
                            </p>
                          </div>

                          {/* <div className="caf-field-box">
                            <p className="caf-field-label">Latitude</p>

                            <p className="caf-field-value">
                              {addressItem.address_latitude || "-"}
                            </p>
                          </div>
 
                          <div className="caf-field-box">
                            <p className="caf-field-label">Longitude</p>

                            <p className="caf-field-value">
                              {addressItem.address_longitude || "-"}
                            </p>
                          </div>
 
                          <div className="caf-field-box">
                            <p className="caf-field-label">Stage</p>

                            <p className="caf-field-value">
                              {addressItem.stage
                                ? addressItem.stage.replaceAll("_", " ")
                                : "-"}
                            </p>
                          </div> */}

                          {/* Status */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Status</p>

                            <p className="caf-field-value">
                              {addressItem.status || "-"}
                            </p>
                          </div>

                          {/* Period of Stay */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Period of Stay</p>

                            <p className="caf-field-value">
                              {addressItem.period_of_stay || "-"}
                            </p>
                          </div>

                          {/* Ownership Status */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Ownership Status</p>

                            <p className="caf-field-value">
                              {addressItem.ownership_status || "-"}
                            </p>
                          </div>

                          {/* Verifier Name */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Verifier Name</p>

                            <p className="caf-field-value">
                              {addressItem.verifier_full_name || "-"}
                            </p>
                          </div>

                          {/* Verifier Relationship */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">
                              Verifier Relationship
                            </p>

                            <p className="caf-field-value">
                              {addressItem.verifier_relationship || "-"}
                            </p>
                          </div>

                          {/* Verifier Landmark */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Verifier Landmark</p>

                            <p className="caf-field-value">
                              {addressItem.verifier_addrlandmark || "-"}
                            </p>
                          </div>

                          {/* Live Selfie Verified */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">
                              Live Selfie Verified
                            </p>

                            <p className="caf-field-value">
                              {addressItem.live_selfie_verified ? "Yes" : "No"}
                            </p>
                          </div>

                          {/* Live Selfie */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Live Selfie</p>

                            {addressItem.live_selfie_url ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.live_selfie_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* Digital Signature */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Digital Signature</p>

                            {addressItem.digital_signature_url ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.digital_signature_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* ID Proof */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">ID Proof</p>

                            {addressItem.id_proof_photo ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.id_proof_photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* Address Proof */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Address Proof</p>

                            {addressItem.address_proof_photo ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.address_proof_photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* House Photo */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">House Photo</p>

                            {addressItem.house_photo ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.house_photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* Locality Photo */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Locality Photo</p>

                            {addressItem.locality_photo ? (
                              <a
                                style={{ color: "#f26522" }}
                                className="d-flex align-items-center gap-1"
                                href={`${apibaseurl}${addressItem.locality_photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <p className="caf-field-value">-</p>
                            )}
                          </div>

                          {/* Created At */}
                          <div className="caf-field-box">
                            <p className="caf-field-label">Created At</p>

                            <p className="caf-field-value">
                              {addressItem.created_at
                                ? dayjs(addressItem.created_at).format(
                                    "DD-MM-YYYY",
                                  )
                                : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      onClick={handleDownloadAddressExcel}
                      disabled={!address || address.length === 0}
                      className="caf-btn caf-btn-primary"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <DownloadOutlined />
                      Export to Excel
                    </button>
                  </div>
                </div>
              )}
              
              {activeTab === "consent" && (
                <div className="px-1 pb-4 text-center text-muted">
                  <div className="caf-consent-frame-wrap">
                    {consentLoading ? (
                      <Loading />
                    ) : consentError ? (
                      <div className="py-5">
                        <p className="mb-3">{consentError}</p>
                        <button
                          type="button"
                          className="caf-btn caf-btn-primary"
                          onClick={() => setConsentReload((n) => n + 1)}
                        >
                          Retry
                        </button>
                      </div>
                    ) : consentPdf ? (
                      <iframe
                        key={consentPdf}
                        src={consentPdf}
                        title="Consent PDF"
                        width="100%"
                        height="450"
                        style={{ border: "none" }}
                      />
                    ) : (
                      <div className="py-5">
                        Consent document not available.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* --- document --- */}
              <style>{`
  .caf-doc-groups {
    display: flex;
    flex-direction: row;
    gap: 1.25rem;
    justify-content: start;
  }

  .caf-doc-group{
  width: 320px;
  }

  .caf-doc-group-label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8a90a0;
    margin-bottom: 0.6rem;
  }

  .caf-doc-files {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 0.9rem;
  }

  .caf-file-card {
    display: flex;
    flex-direction: column;
    position: relative;
    border: 1px solid #eef0f3;
    border-radius: 12px;
    padding: 0.9rem;
    background-color: #ffffff;
    cursor: pointer;
    transition: box-shadow 0.2s ease, transform 0.15s ease, border-color 0.2s ease;
  }
  .caf-file-card:hover {
    box-shadow: 0 6px 16px rgba(20, 20, 43, 0.09);
    transform: translateY(-2px);
    border-color: #d9dcff;
  }
  .caf-file-card:focus-visible {
    outline: 2px solid #4338ca;
    outline-offset: 2px;
  }

  .caf-file-icon {
    font-size: 1.1rem;
    color: #4338ca;
    margin-bottom: 0.4rem;
  }

  .caf-file-preview {
    width: 290px;
    height: 160px;
    max-width: 100%;
    border-radius: 8px;
    background-color: #f4f5f8;
    margin-bottom: 0.5rem;
  }

  .caf-file-preview-image {
    aspect-ratio: 4 / 3;
    overflow: hidden;
    position: relative;
  }
  .caf-file-preview-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.25s ease;
  }
  .caf-file-preview-image:hover img {
    transform: scale(1.04);
  }

  .caf-file-preview-broken,
  .caf-file-preview-generic {
    padding: 1.1rem 0.5rem;
    text-align: center;
    color: #9098a8;
  }
  .caf-file-preview-broken p,
  .caf-file-preview-generic p {
    margin: 0.3rem 0 0;
    font-size: 0.7rem;
  }

  .caf-file-preview-pdf {
    padding: 0.85rem 0.5rem;
    text-align: center;
  }
  .caf-file-preview-pdf .caf-file-name {
    margin: 1.3rem 0;
    font-size: 0.72rem;
    color: #2b2f38;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .caf-pdf-actions {
    display: flex;
    gap: 0.4rem;
    justify-content: center;
  }
  .caf-pdf-actions .caf-btn {
    padding: 3px 10px;
    font-size: 0.72rem;
    border-radius: 7px;
    font-weight: 600;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .caf-pdf-actions .caf-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }

  .caf-file-meta {
    font-size: 0.74rem;
    color: #9098a8;
    margin: 0 0 0.4rem;
  }

  .caf-scan-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 20px;
    width: fit-content;
  }

  .caf-file-download-icon {
    position: absolute;
    top: 0.7rem;
    right: 0.7rem;
    color: #6b7280;
    transition: color 0.15s ease, opacity 0.15s ease;
  }
  .caf-file-card:hover .caf-file-download-icon {
    color: #4338ca;
    opacity: 1 !important;
  }
              `}</style>

              {activeTab === "candidateDocuments" &&
                (getDocumentAPI?.loading ? (
                  <Loading />
                ) : (
                  <>
                    {candidateDocuments.length === 0 ? (
                      <div className="caf-section-card">
                        <div className="caf-empty-state">
                          No Documents Available
                        </div>
                      </div>
                    ) : (
                      candidateDocuments.map((item: any, idx: number) => {
                        const fileGroups = Object.entries(item.files || {});
                        const totalFiles = fileGroups.reduce(
                          (count, [, files]: any) =>
                            count + (files?.length || 0),
                          0,
                        );

                        return (
                          <div
                            key={item.check ?? idx}
                            className="caf-section-card"
                          >
                            <div className="caf-section-header">
                              <div className="caf-section-header-left">
                                <div className="caf-section-icon">
                                  {getSectionIcon(item.check)}
                                </div>
                                <div className="caf-section-titles">
                                  <p className="caf-section-title">
                                    {formatKey(item.check)}
                                  </p>
                                  <p className="caf-section-subtitle">
                                    Uploaded documents
                                  </p>
                                </div>
                              </div>
                              <span className="caf-status-badge">
                                <span className="dot" />
                                {totalFiles} File{totalFiles === 1 ? "" : "s"}
                              </span>
                            </div>

                            <div className="caf-doc-groups">
                              {fileGroups.map(([docType, files]: any) => (
                                <div className="caf-doc-group" key={docType}>
                                  <p className="caf-doc-group-label">
                                    {formatKey(docType)}
                                  </p>

                                  {!files || files.length === 0 ? (
                                    <div className="caf-empty-state">
                                      <FileUnknownOutlined
                                        style={{ marginRight: 6 }}
                                      />
                                      No document available
                                    </div>
                                  ) : (
                                    <div className="caf-doc-files">
                                      {(files || []).map((file: any) => {
                                        const scanMeta = getScanStatusMeta(
                                          file.scan_status,
                                        );
                                        const previewType = getPreviewFileType(
                                          file.mime_type,
                                          file.original_name,
                                        );
                                        const fileUrl = getFileUrl(file);
                                        const isBrokenImage =
                                          brokenImageIds.has(file.file_id);

                                        return (
                                          <div
                                            className="caf-file-card"
                                            key={file.file_id}
                                            role="button"
                                            tabIndex={0}
                                            title={`Download ${file.original_name}`}
                                            onClick={() =>
                                              handleDownloadFile(file)
                                            }
                                            onKeyDown={(e) => {
                                              if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                              ) {
                                                handleDownloadFile(file);
                                              }
                                            }}
                                          >
                                            <div className="caf-file-icon">
                                              {getFileIcon(
                                                file.mime_type,
                                                file.original_name,
                                              )}
                                            </div>
                                            <div className="caf-file-info">
                                              {previewType === "image" &&
                                                !isBrokenImage && (
                                                  <div
                                                    className="caf-file-preview caf-file-preview-image"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      window.open(
                                                        fileUrl,
                                                        "_blank",
                                                        "noopener,noreferrer",
                                                      );
                                                    }}
                                                  >
                                                    <img
                                                      src={fileUrl}
                                                      alt={
                                                        file.original_name ||
                                                        "document preview"
                                                      }
                                                      onError={() =>
                                                        markImageBroken(
                                                          file.file_id,
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )}

                                              {previewType === "image" &&
                                                isBrokenImage && (
                                                  <div className="caf-file-preview caf-file-preview-broken">
                                                    <FileImageOutlined
                                                      style={{ fontSize: 22 }}
                                                    />
                                                    <p>Preview unavailable</p>
                                                  </div>
                                                )}

                                              {previewType === "pdf" && (
                                                <div className="caf-file-preview caf-file-preview-pdf">
                                                  <FilePdfOutlined
                                                    style={{
                                                      fontSize: 24,
                                                      color: "#d32f2f",
                                                    }}
                                                  />
                                                  <p
                                                    className="caf-file-name"
                                                    title={file.original_name}
                                                  >
                                                    {file.original_name ||
                                                      "PDF Document"}
                                                  </p>
                                                  <div className="caf-pdf-actions">
                                                    <button
                                                      type="button"
                                                      className="caf-btn caf-btn-secondary"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(
                                                          fileUrl,
                                                          "_blank",
                                                          "noopener,noreferrer",
                                                        );
                                                      }}
                                                    >
                                                      <EyeOutlined /> Preview
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="caf-btn caf-btn-primary"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownloadFile(
                                                          file,
                                                        );
                                                      }}
                                                    >
                                                      <DownloadOutlined />{" "}
                                                      Download
                                                    </button>
                                                  </div>
                                                </div>
                                              )}

                                              {previewType === "other" && (
                                                <div className="caf-file-preview caf-file-preview-generic">
                                                  <FileUnknownOutlined
                                                    style={{ fontSize: 22 }}
                                                  />
                                                  <p>No preview available</p>
                                                </div>
                                              )}

                                              <p className="caf-file-meta">
                                                {formatFileSize(
                                                  file.size_bytes,
                                                )}
                                                {" • "}
                                                {formatFileDate(
                                                  file.uploaded_at,
                                                )}
                                              </p>
                                              {/* <span
                                              className={`caf-scan-badge ${scanMeta?.className}`}
                                            >
                                              {scanMeta?.icon}
                                              {scanMeta?.label}
                                            </span> */}
                                            </div>
                                            <div className="caf-file-download-icon">
                                              <DownloadOutlined />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateApplicationForm; 